import { Property } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { usePropertyContext } from "../usePropertyContext";
import { useState } from "react";
import { ElementsGroupByPosition } from "../context";
import { PostElement } from "src/api/client-api/post/postSchema";
import { useFillDefaultsMultivalueProperty } from "src/api/client-api/property/useFillDefaultsMultivalueProperty";
import { computeElementsGroup, MultiPropertyGroup } from "../utils/computeElementsGroup";
import { DimensionValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { isPropertyHidden } from "src/utils/ssq";
import { useQuestionnaireFormContext } from "src/components/QuestionnaireForm/context/useQuestionnaireForm";

type Options = {
    property: Property;
    dimensionValue: DimensionValue | undefined;
};
export const useMultivalueProperty = (options: Options): MultivaluePropertyHandler => {
    const { property, dimensionValue } = options;
    const { changePost, getElementsGroups, post, onDelete, onUpdate, ssqSsqHeadingSsqProperty, ssqVersionStructureId } =
        usePropertyContext();
    const { ingredientGenericValues } = useQuestionnaireFormContext();
    const fillDefaultsMultivalueProperty = useFillDefaultsMultivalueProperty();

    const childProperties = (property.children ?? [])
        .filter(
            (property) =>
                !property.hidden && !isPropertyHidden(property.id, post?.hiddenElements ?? [], ssqVersionStructureId),
        )
        .sort((propertyA: Property, propertyB: Property) => (propertyA.position ?? 0) - (propertyB.position ?? 0));

    const initialElements = getElementsGroups(childProperties);
    const [elements, setElements] = useState<ElementsGroupByPosition>(initialElements.elementsGroupByPosition);

    const addElementsGroup = () => {
        const position =
            Object.keys(elements).length ?
                Object.keys(elements).reduce((acc: number, value: string) => Math.max(acc, Number(value)), 0) + 1
            :   0;

        const emptyValues = childProperties.map((childProperty: Property): PostElement => {
            const defaultDataListOptionIds =
                childProperty.dataList ?
                    childProperty.defaultValue ?
                        childProperty.defaultValue.split("|").map(Number)
                    :   []
                :   [];

            return {
                // @ts-expect-error This line is the reason we have complexified the types of PostElement with PostElement | PostElementWrite everywhere
                id: undefined,

                ssqPropertyId: childProperty.id,
                value: childProperty.defaultValue ?? "",
                position: position,
                dataListOptionCodes: childProperty.dataList?.options
                    .filter((option) => defaultDataListOptionIds.includes(option.id))
                    .map((option) => option.code),
            };
        });

        setElements({
            ...elements,
            [position]: emptyValues,
        });

        fillDefaultsMultivalueProperty.mutate(
            {
                postId: post?.id,
                propertyId: property.id,
                position: position,
            },
            {
                onSuccess: () => {
                    changePost();
                },
            },
        );
    };

    const deleteElementsGroup = (position: number) => {
        if (!post) {
            return;
        }

        const { [position]: removedElement, ...updatedElements } = elements;

        setElements(updatedElements);

        if (!onDelete) {
            return;
        }

        property.children?.forEach((childProperty: Property) => {
            onDelete!({
                postId: post.id,
                propertyId: Number(childProperty.id),
                position: position,
            });
        });
    };

    const markAsNotApplicable = (isNotApplicable: boolean) => {
        if (!post || !onUpdate || !onDelete) {
            return;
        }

        const postId = post.id;
        const propertyId = Number(property.id);

        if (isNotApplicable) {
            onUpdate({
                postId: postId,
                propertyId: propertyId,
                value: "n/a",
            });

            return;
        }

        onDelete({
            postId: postId,
            propertyId: propertyId,
        });
    };

    const elementsGroup = computeElementsGroup({
        childProperties,
        dimensionValue,
        elementsMapping: elements,
        ssqSsqHeadingSsqProperty,
        ssqVersionStructureId,
        ingredientGenericValues,
    });

    return {
        addElementsGroup,
        deleteElementsGroup,
        elementsGroup,
        // elements,
        markAsNotApplicable,
    };
};

export type AddElementGroupAction = () => void;
export type DeleteElementGroupAction = (position: number) => void;
export type MarkAsNotApplicableAction = (isNotApplicable: boolean) => void;

export type MultivaluePropertyHandler = {
    addElementsGroup: AddElementGroupAction;
    deleteElementsGroup: DeleteElementGroupAction;
    // elements: ElementsGroupByPosition;
    elementsGroup: MultiPropertyGroup[];
    markAsNotApplicable: MarkAsNotApplicableAction;
};
